import {
    HTTP_INTERCEPTORS,
    HttpClientJsonpModule,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, importProvidersFrom, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// ROUTING

import { AppRoutingModule } from './app-routing.module';

// NGX PERMISSIONS
import { NgxMaskModule } from 'ngx-mask';
import { NgxPermissionsModule } from 'ngx-permissions';

// HASH LOCATION RELOAD

import { CurrencyPipe, LocationStrategy, PathLocationStrategy } from '@angular/common';

// COMPONENTS

import { AppComponent } from './app.component';

// INTERCEPTORS

import { HttpHeadersInterceptor } from './interceptors/http-headers.interceptor';
import { HttpUnauthorizedInterceptor } from './interceptors/http-unauthorized.interceptor';

// DIRECTIVES

import { CatchEventsModule } from './directives/catch-events.module';

//MODULE

import {
    DEFAULT_IGNORE_PATTERNS,
    KeycloakService,
    LaunchContextProvider,
    provideKeycloakAngular,
    provideLogger,
    SPRINTPAY_SERVICES_TOKEN,
    SprintPayAPIsModule,
    StatModule,
} from '@cargos/sprintpay-services';
import { MaintenanceShellComponent, provideExternalLinksLaunch } from '@cargos/sprintpay-ui';
import { PasswordModule } from './pages/auth/password/password.module';
import { TokenService } from './services/utils/token.service';

// Google Maps
import { GoogleMapsModule } from '@angular/google-maps';

// Google Analytics

import { environment } from 'src/environments/environment';
import { createGoogleAnalyticsConfig, GoogleAnalyticsModule } from './modules/google-analytics';
import { Config } from './utils/config';

// SERVICES

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isConfigPresent, isDatadogEnabled } from '@cargos/sprintpay-tools';
import { RumEvent } from '@datadog/browser-rum';
import { browserTracingIntegration } from '@sentry/angular';
import { ApplyForCargoFacilityModule } from './pages/auth/signup/apply-for-cargo-facility/apply-for-cargo-facility.module';
import { UrlService } from './services/url.service';
import { ApplicationErrorHandlerService } from './services/utils/application-error-handler.service';
import { openerServicesProviders } from './shared/providers/opener-services.provider';
import { scrubPII } from './utils/pii-scrubber';

const datadogEnabled = isDatadogEnabled(
    environment.datadog?.enabled ?? false,
    environment.datadog?.clientToken,
    environment.datadog?.applicationId
);
const DATADOG_ENV_BY_ENVIRONMENT: Record<string, string> = {
    develop: 'qa',
    staging: 'uat',
    'staging-dr': 'uat',
    production: 'prod',
    'production-dr': 'prod',
};

const datadogEnv = DATADOG_ENV_BY_ENVIRONMENT[environment.environment] ?? environment.environment;
@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        CatchEventsModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        NgxPermissionsModule.forRoot(),
        PasswordModule,
        ApplyForCargoFacilityModule,
        HttpClientJsonpModule,
        NgxMaskModule.forRoot(),
        SprintPayAPIsModule.forRoot({
            environment: environment.environment,
            tokenGetter: {
                tokenProvider: {
                    provide: SPRINTPAY_SERVICES_TOKEN,
                    useFactory: tokenFactory,
                    deps: [TokenService],
                },
            },
            config: {
                apisWithoutHeaders: Config.apisWithoutHeader,
                apisWithoutContentType: Config.apisWithoutContentType,
                apisWithoutAccessControl: Config.apisWithoutAccessControl,
                allowedDomains: Config.allowedDomains,
                googleMapApiKey: environment.googleMapApiKey,
            },
        }),
        GoogleAnalyticsModule.forRoot(createGoogleAnalyticsConfig(environment)),
        GoogleMapsModule,
        StatModule.forRoot({
            enableStatsig: isConfigPresent(environment.sdkKey),
            sdkKey: environment.sdkKey,
            user: {},
            options: {
                environment: {
                    tier: environment.environment === 'develop' ? 'development' : environment.environment,
                },
            },
        }),
        MaintenanceShellComponent,
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: HttpHeadersInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpUnauthorizedInterceptor, multi: true },
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: ErrorHandler, useClass: ApplicationErrorHandlerService },
        UrlService,
        LaunchContextProvider,
        ...provideExternalLinksLaunch(),
        CurrencyPipe,
        provideKeycloakAngular(environment.keycloakConfig),
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideClientHydration(),
        importProvidersFrom(FormsModule, ReactiveFormsModule),
        KeycloakService,
        provideLogger({
            type: 'multi',
            sentry: {
                enabled: environment.enableSentry,
                dsn: environment.appSentryDsn,
                environment: environment.environment,
                release: environment.appVersion,
                ignorePatterns: DEFAULT_IGNORE_PATTERNS,
                // Messages sent only to DataDog (not to Sentry)
                ignoreErrors: [/^credit_card_verification_/],
                integrations: [browserTracingIntegration()],
                tracesSampleRate: 0.1,
                tracePropagationTargets: ['https://sprintpay.cargosprint.com/'],
            },
            datadog: {
                enabled: datadogEnabled, //Set to false to disable Datadog logging. Change to true to enable.
                site: environment.datadog?.site || '',
                clientToken: environment.datadog?.clientToken || '',
                service: environment.applicationName ?? '',
                env: datadogEnv,
                version: environment.appVersion,
                forwardErrorsToLogs: environment.datadog?.logger?.forwardErrorsToLogs ?? true,
                sessionSampleRate: environment.datadog?.logger?.sessionSampleRate ?? 100,
                rum: {
                    applicationId: environment.datadog?.applicationId || '',
                    clientToken: environment.datadog?.clientToken || '',
                    site: environment.datadog?.site || '',
                    service: environment.applicationName ?? '',
                    env: datadogEnv,
                    version: environment.appVersion,
                    sessionSampleRate: environment.datadog?.rum?.sessionSampleRate ?? 100,
                    sessionReplaySampleRate: environment.datadog?.rum?.sessionReplaySampleRate ?? 20,
                    trackResources: environment.datadog?.rum?.trackResources ?? true,
                    trackUserInteractions: environment.datadog?.rum?.trackUserInteractions ?? true,
                    trackLongTasks: environment.datadog?.rum?.trackLongTasks ?? true,
                    defaultPrivacyLevel: 'mask-user-input',
                    beforeSend: (event: RumEvent) => {
                        if (event.type === 'error' && event.error.message) {
                            event.error.message = scrubPII(event.error.message);
                        }
                        if (event.type === 'view' && event.view.url) {
                            (event.view as { url: string }).url = scrubPII(event.view.url);
                        }
                        if (event.type === 'resource' && event.resource.url) {
                            (event.resource as { url: string }).url = scrubPII(event.resource.url);
                        }
                        return true;
                    },
                },
            },
            enableErrorHandler: true,
            enableSentryTracing: true,
        }),
        ...openerServicesProviders,
    ],
})
export class AppModule {}

function tokenFactory(tokenService: TokenService): () => string | null {
    return () => tokenService.getCurrentUser();
}
